import type * as THREE from 'three';
import { HEROES, PANOPLY, SAGA_CHAPTERS, SKILLS, TOMES, UNIQUES, UNIQUE_FORMS, uniquesFor, type UniqueDef, type UniqueId, type UniqueSlot } from '@somnhold/content';
import { featView, holdsPiece, panoplyPieces, rearmBlock, rearmable, scrapCap, tomeSkill, uniquePieces, type CampaignCommand, type CampaignState, type ItemInstance, type PlayerState } from '@somnhold/sim';
import { audio } from '../audio/sfx.ts';
import { t } from '../i18n.ts';
import { h } from '../ui/dom.ts';
import { ICONS } from '../ui/icons.ts';
import { portraitThumb, thumbLater } from '../ui/thumbs.ts';
import { featGoalText } from './feat-block.ts';
import { offerPin } from './prep-pins.ts';

/*
 * The panoply on screen (docs/PANOPLIA.md §9): «Tus únicas» under a unique slot's header, each piece of the class for
 * that slot as a card (the one worn, those held, the one Hilde can rearm now, those still missing as silhouettes with
 * what they ask), a piece's own sheet, rearming at the forge and the piece's arrival.
 */

type Cmd = (c: CampaignCommand) => Promise<boolean>;

export interface PanoplyCtx {
  s: CampaignState;
  p: PlayerState;
  renderer: THREE.WebGLRenderer;
  cmd: Cmd;
  rerender: () => void;
  /** The forge stands (rearming needs Hilde at it). */
  forge: boolean;
}

/** The piece whose sheet is open under the strip (kept across rerenders). */
let open: UniqueId | null = null;
export const openPiece = (id: UniqueId | null): void => {
  open = id;
};

const SLOT_SCRAPS: Record<UniqueSlot, string> = { weapon: 'panoply.scraps.weapon', armor: 'panoply.scraps.armor', amulet: 'panoply.scraps.amulet' };

/** A piece's card picture: its model, drawn in its turn (a silhouette for a piece still missing, by CSS on the same picture). */
const cardImg = (c: PanoplyCtx, id: UniqueId, size: number, cls = 'pc-img'): HTMLElement => {
  const img = h(`img.${cls}.loading`, { alt: '', draggable: 'false' }) as HTMLImageElement;
  thumbLater(
    c.renderer,
    `unique:${id}`,
    size,
    (url) => {
      img.src = url;
      img.classList.remove('loading');
    },
    () => img.isConnected || !img.ownerDocument,
  );
  return img;
};

/** The saga chapter that gives a slot's saga piece (for a missing one's card). */
const sagaChapterOf = (slot: UniqueSlot): number => SAGA_CHAPTERS.findIndex((ch) => ch.unique === slot) + 1;

/** What a missing piece asks, in a line: its skill to learn (a tome's pages), its scraps, or the saga. */
const missingNeed = (c: PanoplyCtx, d: UniqueDef): string => {
  const h0 = c.p.hero;
  if (!d.rearmed) return t('panoply.fromSaga', { n: sagaChapterOf(d.slot) });
  const button = SKILLS[d.skill as NonNullable<UniqueDef['skill']>].slot;
  if (!rearmable(h0, d.slot).some((x) => x.id === d.id)) {
    if (tomeSkill(h0, button) === d.skill) return t('panoply.needTome', { skill: t(`skill.${d.skill}`), n: h0.tomePages?.[button] ?? 0, m: TOMES.pages });
    return t('panoply.needSkill', { skill: t(`skill.${d.skill}`) });
  }
  return t('panoply.needScraps', { n: h0.scraps?.[d.slot] ?? 0, m: PANOPLY.cost, what: t(SLOT_SCRAPS[d.slot]) });
};

/** The pieces of a slot as the strip lists them: the one worn, those held, the one ready to rearm, those missing. */
const order = (c: PanoplyCtx, slot: UniqueSlot): { d: UniqueDef; state: 'worn' | 'held' | 'ready' | 'missing'; item?: ItemInstance }[] => {
  const h0 = c.p.hero;
  const held = uniquePieces(h0);
  const ready = new Set(rearmable(h0, slot).map((d) => d.id));
  const enough = (h0.scraps?.[slot] ?? 0) >= PANOPLY.cost;
  const rank = { worn: 0, held: 1, ready: 2, missing: 3 } as const;
  return uniquesFor(h0.heroClass, slot)
    .map((d) => {
      const item = held.find((x) => x.unique === d.id);
      const state = item ? (h0.gear[slot]?.id === item.id ? 'worn' : 'held') : ready.has(d.id) && enough ? 'ready' : 'missing';
      return { d, state, ...(item ? { item } : {}) } as const;
    })
    .sort((a, b) => rank[a.state] - rank[b.state]);
};

/** Whether a skill is on its button in the preparation the sheet shows (the one of lo puesto: the first). */
const carriedSkill = (c: PanoplyCtx, skill: UniqueDef['skill']): boolean => !!skill && (c.p.hero.loadouts?.[0]?.picks ?? []).some((x) => x.skill === skill);

/**
 * «Tus únicas» for a unique slot: the strip of cards, the band when they don't come yet, and the open piece's sheet.
 * Empty for a slot that isn't a unique one.
 */
export const panoplyStrip = (c: PanoplyCtx, slot: UniqueSlot): HTMLElement | '' => {
  const h0 = c.p.hero;
  const mine = panoplyPieces(h0, slot);
  const scraps = h0.scraps?.[slot] ?? 0;
  const box = h('section.panoply', { 'data-test': 'panoply', 'data-slot': slot });
  const head = h('div.pn-head', {}, h('span.pn-title', {}, t('panoply.yours')), h('span.pn-scraps', { 'data-test': 'scraps', title: t('panoply.scrapsHint') }, h('span', { html: ICONS.shard }), t('panoply.scrapsCount', { what: t(SLOT_SCRAPS[slot]), n: scraps, m: scrapCap(h0, slot) })));
  if (!h0.uniqueSlots?.[slot]) {
    if (!mine.length) return '';
    box.append(h('p.pn-band', { 'data-test': 'panoply-later' }, t(`panoply.later.${slot}`)));
    return box;
  }
  if (!mine.length) {
    box.append(head, h('p.pn-band', { 'data-test': 'panoply-soon' }, t('panoply.studying')));
    return box;
  }
  // The cellars' last scrap: it went to this slot, and can move once to another that takes it (docs/PANOPLIA.md §4).
  if (h0.cellarsScrap === slot) {
    const to = (['weapon', 'armor', 'amulet'] as const).filter((k) => k !== slot && !!h0.uniqueSlots?.[k] && (h0.scraps?.[k] ?? 0) < scrapCap(h0, k));
    if (to.length)
      box.append(
        h(
          'p.small.pn-move',
          { 'data-test': 'scrap-move' },
          t('panoply.cellarsWent', { what: t(SLOT_SCRAPS[slot]) }),
          ...to.map((k) => h('button.btn.small.ghost', { onclick: async () => { if (await c.cmd({ t: 'scrapSlot', slot: k })) c.rerender(); } }, t('panoply.moveScrap', { what: t(SLOT_SCRAPS[k]) }))),
        ),
      );
  }
  const list = order(c, slot);
  const strip = h('div.pn-strip', { role: 'list' });
  for (const { d, state, item } of list) {
    const on = open === d.id;
    const skill = d.skill ? t(`skill.${d.skill}`) : t('panoply.noSkill');
    strip.append(
      h(
        `button.uqcard.${state}${on ? '.sel' : ''}`,
        {
          role: 'listitem',
          'data-test': 'pcard',
          'data-unique': d.id,
          'data-state': state,
          'aria-pressed': String(on),
          title: item || state === 'ready' ? t(`unique.${d.id}`) : missingNeed(c, d),
          onclick: () => {
            open = on ? null : d.id;
            c.rerender();
          },
        },
        cardImg(c, d.id, 128),
        h('span.pc-skill', {}, skill),
        carriedSkill(c, d.skill) ? h('span.pc-carried', { title: t('panoply.carried'), html: ICONS.check }) : '',
        state === 'missing' ? h('span.pc-need', {}, missingNeed(c, d)) : state === 'ready' ? h('span.pc-need.ready', {}, t('panoply.canRearm')) : '',
      ),
    );
  }
  box.append(head, strip);
  // Hilde can rearm one now: the forge's own section, with the pieces as big cards.
  const can = list.filter((x) => x.state === 'ready');
  if (can.length) box.append(rearmSection(c, slot, can.map((x) => x.d)));
  if (open && list.some((x) => x.d.id === open)) box.append(pieceSheet(c, open));
  return box;
};

/** «Rearmar»: six scraps of the slot, Hilde at the forge, a piece of a skill the hero knows (docs/PANOPLIA.md §9.2). */
const rearmSection = (c: PanoplyCtx, slot: UniqueSlot, ds: UniqueDef[]): HTMLElement =>
  h(
    'div.pn-rearm',
    { 'data-test': 'rearm' },
    h('p.pn-rearm-cap', {}, h('span', { html: ICONS.sparkle }), t('panoply.rearmCap', { what: t(SLOT_SCRAPS[slot]) })),
    c.forge ? '' : h('p.small.hint', {}, t('forge.ruined')),
    h(
      'div.pn-rearm-cards',
      {},
      ...ds.map((d) =>
        h(
          'button.pn-rearm-card',
          {
            'data-test': 'rearm-go',
            'data-unique': d.id,
            disabled: !c.forge || !!rearmBlock(c.s, c.p.hero, d.id),
            onclick: async () => {
              if (!(await c.cmd({ t: 'rearm', unique: d.id }))) return;
              const item = uniquePieces(c.p.hero).find((x) => x.unique === d.id);
              open = d.id;
              c.rerender();
              if (item) rearmReveal(c, item);
            },
          },
          cardImg(c, d.id, 192, 'pr-img'),
          h('span.pr-txt', {}, h('b', {}, t(`unique.${d.id}`)), h('span.small', {}, d.skill ? t(`skill.${d.skill}`) : ''), h('span.small.pr-power', {}, t(`unique.${d.id}.desc`))),
        ),
      ),
    ),
  );

/** A piece's own sheet under the strip: its power, its feat with it, its Legend, its final form, Hilde's line, put it on. */
const pieceSheet = (c: PanoplyCtx, id: UniqueId): HTMLElement => {
  const h0 = c.p.hero;
  const d = UNIQUES[id];
  const item = uniquePieces(h0).find((x) => x.unique === id);
  const box = h('div.pn-sheet', { 'data-test': 'piece-sheet', 'data-unique': id });
  box.append(h('div.ps-head', {}, cardImg(c, id, 256, 'ps-img'), h('div.ps-title', {}, h('h5', {}, t(`unique.${id}`)), h('span.small', {}, d.skill ? t('panoply.ofSkill', { skill: t(`skill.${d.skill}`) }) : t('panoply.noSkill')))));
  box.append(h('p.small.effect.unique-fx', {}, t(`unique.${id}.desc`)));
  const f = featView(h0, id);
  if (item && f && f.step <= 4) {
    box.append(h('p.small.ps-feat', {}, h('b', {}, `${t(`unique.${id}.wants`)}: `), featGoalText(c.s, h0, id, f.step), f.step <= 3 ? ` · ${f.n} / ${f.goal}` : ''));
  }
  const lg = h0.legends?.[id];
  if (item && lg && !lg.done) box.append(h('p.small.ps-legend', {}, t('panoply.legend', { n: lg.bosses.length, m: 3 })));
  const fin = UNIQUE_FORMS[id].final;
  box.append(h('p.small.ps-final', {}, h('b', {}, `${t(`uform.${fin.id}`)}: `), t(`uform.${fin.id}.desc`), lg?.done ? h('span.ps-final-on', {}, ` · ${t('panoply.finalWon')}`) : ''));
  const lore = t(`unique.${id}.lore`);
  if (hasLore(id)) box.append(h('div.ps-lore', {}, h('img.ps-hilde', { alt: '', src: portraitThumb(c.renderer, 'hilde', 96) }), h('p.small', {}, lore)));
  if (item && h0.gear[d.slot]?.id !== item.id) {
    const put = async (withSkill: boolean): Promise<void> => {
      if (!(await c.cmd({ t: 'equip', item: item.id }))) return;
      let hero = h0;
      if (withSkill && d.skill) {
        const l = h0.loadouts?.[0];
        const picks = (l?.picks ?? HEROES[h0.heroClass].skills.map((sk) => ({ skill: sk, variant: null }))).map((x) => ({ ...x })) as NonNullable<typeof l>['picks'];
        picks[SKILLS[d.skill].slot] = { skill: d.skill, variant: null };
        // If it keeps a piece in that slot, it keeps this one now: the piece goes with its skill.
        const next = { ...(l ?? {}), picks, ...(l?.gear?.[d.slot] !== undefined ? { gear: { ...l.gear, [d.slot]: item.id } } : {}) };
        if (await c.cmd({ t: 'loadout', index: 0, loadout: next })) hero = { ...h0, loadouts: [next, ...(h0.loadouts ?? []).slice(1)] };
      }
      offerPin(hero, d.slot, item, c.cmd, c.rerender);
      audio.play('build', 0.4);
      c.rerender();
    };
    box.append(
      h(
        'div.ps-acts',
        {},
        h('button.btn.small', { 'data-test': 'piece-wear', onclick: () => void put(false) }, t('panoply.wear')),
        d.skill && !carriedSkill(c, d.skill) ? h('button.btn.small.stone', { 'data-test': 'piece-wear-skill', onclick: () => void put(true) }, t('panoply.wearWith', { skill: t(`skill.${d.skill}`) })) : '',
      ),
    );
  }
  if (item) box.append(h('button.btn.small.ghost.ps-again', { 'data-test': 'piece-again', onclick: () => rearmReveal(c, item, true) }, t('panoply.again')));
  else if (!holdsPiece(h0, id)) box.append(h('p.small.hint', {}, missingNeed(c, d)));
  return box;
};

/** Whether a piece has Hilde's line (the saga's weapons don't: their story is the saga's). */
const hasLore = (id: UniqueId): boolean => {
  const key = `unique.${id}.lore`;
  const txt = t(key);
  return !!txt && txt !== key;
};

/** How many turns the arrival's miniature has (one picture each, baked once). */
const TURNS = 16;

/**
 * The piece arriving (docs/PANOPLIA.md §9.3): charcoal and sparks, the scraps falling on the anvil and joining in a
 * dragon-red flash, the piece turning with its name and skill, Hilde's line in her card, and put it on or keep it.
 * Six seconds at most; a touch skips it after half a second. Without motion: plain cuts.
 */
export const rearmReveal = (c: PanoplyCtx, item: ItemInstance, again = false): void => {
  const id = item.unique as UniqueId;
  const d = UNIQUES[id];
  const still = typeof matchMedia === 'function' && matchMedia('(prefers-reduced-motion: reduce)').matches;
  const frames: string[] = [];
  const turn = h('img.rr-piece', { alt: '', draggable: 'false' }) as HTMLImageElement;
  // Open until closed (the pictures stop baking then).
  let shown = true;
  // The turns, baked one a frame while the sparks rise (never all at once: docs/RENDIMIENTO.md).
  for (let i = 0; i < (still ? 1 : TURNS); i++)
    thumbLater(
      c.renderer,
      `unique:${id}`,
      320,
      (url) => {
        frames[i] = url;
        if (i === 0) turn.src = url;
      },
      () => shown,
      (i / TURNS) * Math.PI * 2,
    );
  const start = performance.now();
  let raf = 0;
  const spin = (): void => {
    const k = Math.floor(((performance.now() - start) / 260) % TURNS);
    const f = frames[k];
    if (f && turn.src !== f) turn.src = f;
    raf = requestAnimationFrame(spin);
  };
  let auto = 0;
  const close = (): void => {
    shown = false;
    cancelAnimationFrame(raf);
    window.clearTimeout(auto);
    back.remove();
  };
  const back = h(
    `div.rr-back${still ? '.still' : ''}${again ? '.again' : ''}`,
    { role: 'dialog', 'aria-modal': 'true', 'aria-label': t(`unique.${id}`), 'data-test': 'rearm-reveal', onclick: (e: Event) => { if (performance.now() - start > 500 && e.target === back) close(); } },
    h('div.rr-sparks', { 'aria-hidden': 'true' }, ...Array.from({ length: 14 }, (_, i) => h('i', { style: `--i:${i}` }))),
    again ? '' : h('div.rr-scraps', { 'aria-hidden': 'true' }, h('i'), h('i'), h('i'), h('i')),
    h('div.rr-flash', { 'aria-hidden': 'true' }),
    h('div.rr-stage', {}, turn),
    h('div.rr-name', {}, h('h3', {}, t(`unique.${id}`)), h('span', {}, d.skill ? t('panoply.ofSkill', { skill: t(`skill.${d.skill}`) }) : '')),
    // Hilde's line, when the piece has one (the saga's weapons don't).
    hasLore(id) ? h('div.rr-hilde', {}, h('img', { alt: '', src: portraitThumb(c.renderer, 'hilde', 96) }), h('p', {}, t(`unique.${id}.lore`))) : '',
    h(
      'div.rr-acts',
      {},
      c.p.hero.gear[d.slot]?.id === item.id
        ? ''
        : h(
            'button.btn',
            {
              'data-test': 'reveal-wear',
              onclick: async () => {
                close();
                if (await c.cmd({ t: 'equip', item: item.id })) {
                  audio.play('build', 0.4);
                  offerPin(c.p.hero, d.slot, item, c.cmd, c.rerender);
                  c.rerender();
                }
              },
            },
            d.skill && !carriedSkill(c, d.skill) ? t('panoply.wearWith', { skill: t(`skill.${d.skill}`) }) : t('panoply.wear'),
          ),
      h('button.btn.ghost', { 'data-test': 'reveal-keep', onclick: close }, t('panoply.keep')),
    ),
  );
  document.body.append(back);
  if (!still) raf = requestAnimationFrame(spin);
  audio.play(again ? 'click' : 'bell', 0.6);
  // Six seconds at most for the show; the buttons stay until touched.
  auto = window.setTimeout(() => back.classList.add('settled'), 6000);
};
